import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SecondaryButton from '../atoms/SecondaryButton'
import PlusSvg from '../other/svg/PlusSvg'
import { MotorbikePhoto } from 'src/redux/photosReducer'

interface VehiclePhotosPanelProps {
  photos: MotorbikePhoto[]
  onAdd: (file: string) => void
  onDelete: (photoId: string) => void
}

const VehiclePhotosPanel = ({ photos, onAdd, onDelete }: VehiclePhotosPanelProps): JSX.Element => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [photoToDelete, setPhotoToDelete] = useState<string | null>(null)
  const [typeError, setTypeError] = useState(false)

  const pickFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (typeof file === 'undefined') {
      return
    }
    const allowedType = file.type === 'image/jpeg' || file.type === 'image/png'
    if (allowedType) {
      setTypeError(false)
    } else {
      setTypeError(true)
      return
    }
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        onAdd(reader.result)
      }
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="self-stretch bg-neutral-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base inline-flex flex-col justify-start items-start">
      <div className="self-stretch h-14 px-4 py-3 inline-flex justify-between items-center">
        <div className="justify-start text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5">
          {t('my_vehicles.photos')}
        </div>
        <SecondaryButton
          size="base"
          onClick={() => {
            inputRef.current?.click()
          }}
          icon={<PlusSvg className="w-3.5 h-3.5 text-icon-secondary" />}>
          {t('my_vehicles.add_photo')}
        </SecondaryButton>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={pickFile} />
      </div>
      <div className="self-stretch px-4 pb-4 flex flex-col gap-3">
        {typeError && (
          <div className="text-sm font-medium font-Montserrat text-foregrounds-fg-subtle">
            {t('my_vehicles.photo_type_error')}
          </div>
        )}
        {photos.length === 0 ? (
          <div className="text-sm font-medium font-Montserrat text-foregrounds-fg-subtle">
            {t('my_vehicles.photos_empty')}
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {photos.map((photo) => (
              <div key={photo.id} className="relative w-28 h-20">
                <img
                  src={photo.thumb_url ?? photo.url ?? ''}
                  alt=""
                  className="w-28 h-20 rounded-lg object-cover bg-white"
                />
                <button
                  type="button"
                  onClick={() => {
                    setPhotoToDelete(photo.id)
                  }}
                  className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-white text-xs font-Montserrat">
                  {t('my_vehicles.delete')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {photoToDelete !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 max-w-md w-full mx-4 bg-white">
            <div className="text-xl font-semibold font-Montserrat mb-3">{t('my_vehicles.delete_photo_title')}</div>
            <div className="text-sm font-Montserrat text-foregrounds-fg-subtle mb-6">
              {t('my_vehicles.delete_photo_message')}
            </div>
            <div className="flex justify-end gap-3">
              <SecondaryButton
                size="base"
                onClick={() => {
                  setPhotoToDelete(null)
                }}>
                {t('integrations_screen.cancel')}
              </SecondaryButton>
              <SecondaryButton
                size="base"
                onClick={() => {
                  onDelete(photoToDelete)
                  setPhotoToDelete(null)
                }}>
                {t('my_vehicles.delete')}
              </SecondaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VehiclePhotosPanel
